import { Field } from 'app/interfaces/fields';
import { GlobalService } from './global.service';
import { Block } from 'app/interfaces/blocks';
import { translate } from '@jsverse/transloco';
import { FieldValidations } from 'app/main/pages/settings/documentfields/metadata-item.dto';
import { RecipientRole } from 'app/main/pages/edit-template/edit-template.dto';
import * as moment from 'moment';

export function fieldsValidation(fields: Field, globalService: GlobalService, role_id: string|number, anyoneRequired: boolean = false) {
    const isTemplate = !!document.querySelector('.content-wrapper.edit-template, .content-wrapper.content-library');
    if (isTemplate) return { status: true };
    let message = '';
    let isValid = true;
    for (const key in fields) {
        if (fields[key] && document.querySelector(`.drag-fields[uuid="${key}"]`)) {
            const field: Field = fields[key];
            const validation: FieldValidations = field?.validation;
            if ((field.selected_for_role_id == role_id || (anyoneRequired && field.selected_for_role_id == -1)) && field.hidden == 0) {
                const fieldName = getFieldName(field, globalService);
                if (field.field_required == '1' && !field.value) {
                    message = translate("Field required (form validation) - Field name is required", { fieldName });
                    isValid = false;
                } else if (validation?.validation_type && validation?.validation_type) {
                    if (validation['validation_type'] == 'numbers') {
                        if (!new RegExp(/^[0-9.]+$/).test(field.value)) {
                            message = translate("Field required (form validation) - Field name has invalid value", { fieldName });
                            isValid = false;
                        }
                        if (validation['number_validation_type'] == 'numbers_characters') {
                            const { invalid, errorMessage } = createDigitLengthValidator(validation['min'], validation['max'], field.value);
                            if (invalid) {
                                message = `${errorMessage ? translate(errorMessage, { fieldName }) : ''}`;
                                isValid = false;
                            }
                        } else if (validation['number_validation_type'] == 'numbers_range') {
                            const { invalid, errorMessage } = rangeValidator(validation['min'], validation['max'], validation['decimals'], field.value, true);
                            if (invalid || (field.value && !new RegExp(/^[-,0-9 .]+$/).test(field.value))) {
                                message = `${errorMessage ? translate(errorMessage, { fieldName }) : ''}`;
                                isValid = false;
                            }
                        }
                    } else if (validation['validation_type'] == 'letters' || validation['validation_type'] == 'numbers_letters') {
                        const validationRegex = createFieldRegex(validation['validation_type'], field.allow_multi_lines);
                        if ((field.value && !validationRegex.test(field.value)) || String(field.value).length > validation['max']) {
                            message = translate("Field required (form validation) - Field name has invalid value", { fieldName });
                            isValid = false;
                        }
                    } else if (validation['validation_type'] == 'email') {
                        if ((field.value && !new RegExp(/^[\w-\\.]+@([\w-]+\.)+[\w-]{2,4}$/).test(field.value))) {
                            message = translate("Field required (form validation) - Field name has invalid email", { fieldName });
                            isValid = false;
                        }
                    } else if (validation['validation_type'] == 'custom_regex_validation') {
                        let pattern = validation['regular_expression'].toString();
                        if (pattern[0] == '/') {
                            pattern = pattern.slice(1);
                        }
                        if (pattern[pattern.length - 1] == '/') {
                            pattern = pattern.slice(0, -1);
                        }
                        if (field.value && !new RegExp(pattern)) {
                            message = translate("Field required (form validation) - Field name has invalid value", { fieldName });
                            isValid = false;
                        }
                    }
                }
            }
            if (message) { showErrorToast(message, globalService); }
        }
    }
    if (fields['blocks']) {
        for (const key in fields['blocks']) {
            if (fields['blocks'][key]) {
                const block: Block = fields['blocks'][key];
                if (["attachment"].includes(block.type)) {
                    if (block.uploading_is == 1 && block.request_from == role_id && block.files.length == 0  && !block?.hidden) {
                        message = translate("Field required (form validation) - Field name is required", { fieldName: block.attachment_name });
                        isValid = false;
                    }
                    if (role_id != 0 && block.viewing_is == 1 && block.files.length > 0  && !block?.hidden && !block?.viewed_by.includes(Number(role_id))) {
                        const filename = block.files[0]?.filename ?? block.attachment_name;
                        message = translate("Attachment is required to view (form validation) - Please make sure to review the attached file: attachment name", { attachmentName: filename });
                        isValid = false;
                    }
                } else if (["upload-pdf"].includes(block.type)) {
                    if (block.uploading_is == 1 && block.request_from == role_id && block.files.length == 0  && !block?.hidden && block?.is_parent === 1) {
                        message = translate("Field required (form validation) - Field name is required", { fieldName: block.attachment_name });
                        isValid = false;
                    }
                }
                
                if (message) { showErrorToast(message, globalService); }
            }
        }
    }
    return { status: isValid };
}


export function onFieldValidation(field: Field, globalService: GlobalService, role_id: number, anyoneRequired: boolean = false) {
    const fieldObj = {};
    fieldObj[field.uuid] = field;
    const { status } = fieldsValidation(fieldObj, globalService, role_id, anyoneRequired);
    if (!status) {
        (document.querySelector('[uuid="' + field.uuid + '"]') as HTMLElement)?.classList.add('invalid')
    } else {
        (document.querySelector('[uuid="' + field.uuid + '"]') as HTMLElement)?.classList.remove('invalid')
    }
}

/**
 * show the error toast
 * @param message 
 * @param globalService 
 */
export function showErrorToast(message: string, globalService: GlobalService) {
    globalService.toastrError(message);
}


/**
   * Create dynamic decimal validator
   * @param min 
   * @param max 
   * @param decimalPlaces 
   * @param value 
   * @param allowDecimal 
   * @returns ERROR || NULL
   */
export function rangeValidator(min: number, max: number, decimalPlaces: number, value: string, allowDecimal: boolean = false) {
    const parseValue = parseInt(String(value));
    if (String(value) == '') {
        return { invalid: true, errorMessage: "Field required (form validation) - Field name has invalid value" };
    }

    if (isNaN(parseValue)) {
        return { invalid: true, errorMessage: "Field required (form validation) - Field name has invalid value" };
    }

    if (parseValue < min || parseValue > max) {
        return { invalid: true, errorMessage: "Field required (form validation) - Field name has invalid value" };
    }

    const decimals = value.toString().indexOf('.') != -1 ? value.split('.') : null;
    if (allowDecimal && decimals != null && decimals[1].length > decimalPlaces) {
        return { invalid: true, errorMessage: "Field required (form validation) - Field name has invalid decimals" };
    }

    return { invalid: false, errorMessage: '' };
}


export function personFieldsValidation(roles:RecipientRole[], fields) {
    let validate:boolean = false;
    // Loop through each role
    roles.forEach(role => {
        // Combine personfields and orgfields into one array to simplify the search
        const allFields = [...(role.personfields || []), ...(role.orgfields || [])];

        // Loop through the fields object to check for matches
        Object.values(fields).forEach((field: Field) => {
            // Check if any field id matches the id in personfields or orgfields
            if(["person_field", "org_field"].includes(field.field_section)){
                const matchedField = allFields.find(f => f.id == field.selected_field_type_id && f.is_enable == 0 && field.field_required == "1");
                // If a match is found, push it into the matchingFields array
                if (matchedField) {
                    validate = true;    
                }
            }
        });
    });

    return {validate: validate};
}

export function isADate(value: string | number | Date):boolean{
    return moment(value).isValid();
}

export function createDigitLengthValidator(min:number, max:number, value:string) {
    const regex = new RegExp(`^[0-9]{${min},${max}}$`);
    if (String(value) == '') {
        return { invalid: true, errorMessage: "Field required (form validation) - Field name has invalid value" };
    }
    if(!regex.test(value)) {
        return { invalid: true, errorMessage: 'Field required (form validation) - Field name has invalid value' };
    }

    return { invalid: false, errorMessage: '' };
}

export function createFieldRegex(validationType:string, allowMultiLines?:number): RegExp {
    const extraChars = allowMultiLines === 1 ? '\\r\\n ' : ' ';
    if (validationType === 'letters') {
        return new RegExp(`^[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż${extraChars}\\-._]+$`);
    }
    return new RegExp(`^[A-Za-zÀ-ÖØ-öø-ÿĄąĆćĘęŁłŃńÓóŚśŹźŻż0-9${extraChars}\\-._]+$`);
}

export function getFieldName(field: Field, globalService: GlobalService): string {
    return field.select_type_text || globalService.getFieldNameByType(field.selected_field);
}